/**
 * Comprehensive Task Form - Showcases the full power of the Task primitive
 *
 * Features:
 * - 185+ field support from TASK_FIELDS_COMPREHENSIVE_SPEC
 * - Nested task hierarchy (parent-child relationships)
 * - Approval workflows with dual control & MFA
 * - Escalation rules & SLA management
 * - Dependencies & scheduling
 * - Compliance & audit tracking
 * - Real-time collaboration
 * - Life-critical safety validation
 *
 * This form demonstrates what's possible when the Task primitive is fully leveraged.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Collapse,
  ColorInput,
  Divider,
  Grid,
  Group,
  JsonInput,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  Select,
  Slider,
  Stack,
  Switch,
  Tabs,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Timeline,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useListState } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlarm,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowsSplit,
  IconCalendar,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconCode,
  IconCopy,
  IconDatabase,
  IconExternalLink,
  IconEye,
  IconFileCheck,
  IconFingerprint,
  IconFlame,
  IconGavel,
  IconHierarchy,
  IconHistory,
  IconInfoCircle,
  IconLink,
  IconListCheck,
  IconLock,
  IconPlus,
  IconScale,
  IconShield,
  IconShieldCheck,
  IconStar,
  IconTag,
  IconTarget,
  IconTrash,
  IconTrendingUp,
  IconUser,
  IconUserCheck,
  IconUsers,
  IconWebhook,
  IconX,
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTaskMutations } from '../data/hooks/useTask';
import type { EscalationRule, Task } from '../data/repositories/TaskRepository';
import { useCurrentTenant } from '../hooks/useCurrentTenant';
import type { TaskFormState, TaskFormSetters } from './taskFormState';
import { TaskFormToolbar } from './TaskFormToolbar';
import { SaveTemplateModal } from './SaveTemplateModal';
import { TaskTemplatePicker } from './TaskTemplatePicker';
import { TaskPrefillPanel } from './TaskPrefillPanel';
import { applyFormState } from './taskFormState';

// ============================================================================
// TYPES
// ============================================================================

interface ComprehensiveTaskFormProps {
  mode: 'create' | 'edit' | 'clone';
  initialData?: Partial<Task>;
  instanceId?: string;
  parentTaskId?: string; // For nested tasks
  onSuccess?: (task: Task) => void;
  onCancel?: () => void;
  showLifeCriticalFeatures?: boolean; // Enable life-critical validation
  /** Called when Clone is clicked — receives current state with name appended " (Clone)" */
  onClone?: (state: TaskFormState) => void;
  /** Show the toolbar with Clone / Save Template / Load Template */
  templateToolbarEnabled?: boolean;
  /** Show the LLM Prefill button in the toolbar */
  llmPrefillEnabled?: boolean;
  /** Called when LLM Prefill is requested */
  onLlmPrefill?: () => void;
}

interface SubTask {
  id: string;
  name: string;
  taskType: string;
  priority: string;
  assignedTo?: string;
  estimatedDuration?: number;
  sequenceOrder: number;
}

interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

interface DualControlState {
  required: boolean;
  firstSigner?: string;
  firstSignerName?: string;
  firstSignedAt?: string;
  firstSignerMfaVerified: boolean;
  secondSigner?: string;
  secondSignedAt?: string;
  secondSignerMfaVerified: boolean;
}

interface ApprovalDecision {
  approverId: string;
  approverName: string;
  decision: 'approved' | 'rejected' | 'revision_requested';
  reasonCode?: string;
  comments?: string;
  decidedAt: string;
  mfaVerified: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ComprehensiveTaskForm: React.FC<ComprehensiveTaskFormProps> = ({
  mode = 'create',
  initialData,
  instanceId,
  parentTaskId,
  onSuccess,
  onCancel,
  showLifeCriticalFeatures = false,
  onClone,
  templateToolbarEnabled = true,
  llmPrefillEnabled = true,
  onLlmPrefill,
}) => {
  const { createTask, updateTask, loading: mutationLoading } = useTaskMutations();
  const currentTenant = useCurrentTenant();

  // Shorthand for reading from initialData.metadata (where most fields live)
  const _m = initialData?.metadata as Record<string, any> | undefined;
  // Helper: parse ISO date string to Date | null
  const _d = (v: unknown): Date | null => (typeof v === 'string' ? new Date(v) : v instanceof Date ? v : null);

  // ========================================================================
  // CORE IDENTITY FIELDS
  // ========================================================================
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [externalRef, setExternalRef] = useState(_m?.externalRef || '');
  const [taskCategory, setTaskCategory] = useState((initialData as any)?.taskCategory || _m?.taskCategory || '');
  const [domain, setDomain] = useState((initialData as any)?.domain || _m?.domain || '');

  // ========================================================================
  // CLASSIFICATION FIELDS
  // ========================================================================
  const [taskType, setTaskType] = useState<string>(initialData?.taskType || 'user');
  const [tags, setTags] = useState<string[]>((initialData as any)?.tags || _m?.tags || []);

  // ========================================================================
  // STATUS & LIFECYCLE
  // ========================================================================
  const [priority, setPriority] = useState<string>(initialData?.priority || 'normal');
  const [dueDate, setDueDate] = useState<Date | null>(_d((initialData as any)?.dueDate));
  const [deadlineAt, setDeadlineAt] = useState<Date | null>(_d((initialData as any)?.deadlineAt || _m?.deadlineAt));
  const [estimatedDuration, setEstimatedDuration] = useState<number | undefined>((initialData as any)?.estimatedDuration ?? undefined);
  const [statusReason, setStatusReason] = useState(_m?.statusReason || '');
  const [blockedReason, setBlockedReason] = useState(_m?.blockedReason || '');

  // ========================================================================
  // ASSIGNMENT & OWNERSHIP
  // ========================================================================
  const [assignedTo, setAssignedTo] = useState(initialData?.assignedTo || '');
  const [assignedTeam, setAssignedTeam] = useState(initialData?.assignedTeam || '');
  const [assignedRole, setAssignedRole] = useState((initialData as any)?.assignedRole || _m?.assignedRole || '');
  const [assignmentMethod, setAssignmentMethod] = useState<'manual' | 'auto' | 'escalated'>(
    _m?.assignmentMethod || 'manual',
  );
  const [watchers, setWatchers] = useState<string[]>(_m?.watchers || []);
  const [collaborators, setCollaborators] = useState<string[]>(_m?.collaborators || []);
  const [delegates, setDelegates] = useState<string[]>(_m?.delegates || []);

  // ========================================================================
  // URGENCY & CRITICALITY
  // ========================================================================
  const [priorityScore, setPriorityScore] = useState(_m?.priorityScore ?? 50);
  const [priorityReason, setPriorityReason] = useState(_m?.priorityReason || '');
  const [isCritical, setIsCritical] = useState(_m?.isCritical || false);
  const [requiresImmediateAttention, setRequiresImmediateAttention] = useState(_m?.requiresImmediateAttention || false);

  // ========================================================================
  // DEPENDENCIES & HIERARCHY
  // ========================================================================
  const [dependencies, setDependencies] = useState<string[]>(initialData?.dependencies || []);
  const [relatedTasks, setRelatedTasks] = useState<string[]>(_m?.relatedTasks || []);
  const [isParent, setIsParent] = useState(_m?.isParent || false);
  const [childTasks, setChildTasks] = useListState([] as SubTask[]);
  const [completionStrategy, setCompletionStrategy] = useState<string>(
    _m?.completionStrategy || 'all_children_must_complete',
  );

  // ========================================================================
  // FORM & DATA
  // ========================================================================
  const [formSchema, setFormSchema] = useState<string>(_m?.formSchema || '{}');
  const [formVersion, setFormVersion] = useState(_m?.formVersion || '1.0.0');
  const [formTemplateId, setFormTemplateId] = useState(_m?.formTemplateId || '');
  const [contextData, setContextData] = useState<string>(_m?.contextData || '{}');
  const [variables, setVariables] = useState<string>(typeof _m?.variables === 'string' ? _m.variables : '{}');

  // ========================================================================
  // APPROVAL & SECURITY
  // ========================================================================
  const [approvalRequired, setApprovalRequired] = useState(initialData?.approvalRequired || false);
  const [approvalType, setApprovalType] = useState(_m?.approvalType || '');
  const [approvalPolicyId, setApprovalPolicyId] = useState(_m?.approvalPolicyId || '');
  const [approvers, setApprovers] = useState<string[]>(_m?.approvers || []);
  const [approvalDecisions, setApprovalDecisions] = useState<ApprovalDecision[]>(_m?.approvalDecisions || []);

  // Dual Control
  const [dualControl, setDualControl] = useState<DualControlState>(_m?.dualControl || {
    required: false,
    firstSignerMfaVerified: false,
    secondSignerMfaVerified: false,
  });

  // Security
  const [requiresMfa, setRequiresMfa] = useState(_m?.requiresMfa || false);
  const [securityLevel, setSecurityLevel] = useState<string>(_m?.securityLevel || 'public');
  const [accessControlList, setAccessControlList] = useState<string[]>(_m?.accessControlList || []);
  const [requiredPermissions, setRequiredPermissions] = useState<string[]>(_m?.requiredPermissions || []);
  const [requiredRoles, setRequiredRoles] = useState<string[]>(_m?.requiredRoles || []);

  // ========================================================================
  // ESCALATION & SLA
  // ========================================================================
  const [escalationRules, setEscalationRules] = useState<EscalationRule[]>(_m?.escalationRules || []);
  const [escalationChain, setEscalationChain] = useState<string[]>(_m?.escalationChain || []);
  const [slaDeadline, setSlaDeadline] = useState<Date | null>(_d(_m?.slaDeadline));
  const [slaBufferMinutes, setSlaBufferMinutes] = useState<number>(_m?.slaBufferMinutes ?? 30);
  const [notificationChannels, setNotificationChannels] = useState<string[]>(_m?.notificationChannels || ['email']);

  // ========================================================================
  // INTEGRATION & SYSTEM
  // ========================================================================
  const [integrationId, setIntegrationId] = useState(_m?.integrationId || '');
  const [webhookUrl, setWebhookUrl] = useState(_m?.webhookUrl || '');
  const [callbackUrl, setCallbackUrl] = useState(_m?.callbackUrl || '');
  const [isSystemTask, setIsSystemTask] = useState(_m?.isSystemTask || false);
  const [isAutomated, setIsAutomated] = useState(_m?.isAutomated || false);
  const [automationScript, setAutomationScript] = useState(_m?.automationScript || '');
  const [maxRetries, setMaxRetries] = useState<number>(_m?.maxRetries ?? 3);
  const [retryPolicy, setRetryPolicy] = useState<string>(typeof _m?.retryPolicy === 'string' ? _m.retryPolicy : '{}');

  // ========================================================================
  // UI & DISPLAY
  // ========================================================================
  const [uiComponent, setUiComponent] = useState(_m?.uiComponent || '');
  const [icon, setIcon] = useState(_m?.icon || '');
  const [color, setColor] = useState(_m?.color || '#228be6');
  const [displayOrder, setDisplayOrder] = useState<number | undefined>(_m?.displayOrder);
  const [renderMode, setRenderMode] = useState<string>(_m?.renderMode || 'form');
  const [allowComments, setAllowComments] = useState(_m?.allowComments ?? true);
  const [allowAttachments, setAllowAttachments] = useState(_m?.allowAttachments ?? true);
  const [allowDelegation, setAllowDelegation] = useState(_m?.allowDelegation ?? true);
  const [isArchived, setIsArchived] = useState(_m?.isArchived || false);

  // ========================================================================
  // COMPLIANCE & AUDIT
  // ========================================================================
  const [complianceFrameworks, setComplianceFrameworks] = useState<string[]>(_m?.complianceFrameworks || []);
  // T3 QAPI score removed — not allowed in operational forms (DRR-114)
  const [regulatoryRequirements, setRegulatoryRequirements] = useState<string[]>(_m?.regulatoryRequirements || []);
  const [evidenceRequired, setEvidenceRequired] = useState(_m?.evidenceRequired || false);
  const [isConfidential, setIsConfidential] = useState(_m?.isConfidential || false);
  const [isPrivileged, setIsPrivileged] = useState(_m?.isPrivileged || false);
  const [retentionPeriodDays, setRetentionPeriodDays] = useState<number | undefined>(_m?.retentionPeriodDays);
  const [legalHold, setLegalHold] = useState(_m?.legalHold || false);

  // ========================================================================
  // METADATA & EXTENSIBILITY
  // ========================================================================
  const [metadata, setMetadata] = useState<string>('{}');
  const [customFields, setCustomFields] = useState<string>(typeof _m?.customFields === 'string' ? _m.customFields : '{}');
  const [labels, setLabels] = useState<Record<string, string>>(_m?.labels || {});
  const [locale, setLocale] = useState(_m?.locale || 'en-US');
  const [timezone, setTimezone] = useState(_m?.timezone || 'America/New_York');

  // ========================================================================
  // COLLABORATION
  // ========================================================================
  const [draftMode, setDraftMode] = useState(_m?.draftMode || false);
  const [comments, setComments] = useState<any[]>([]);
  const [feedbackScore, setFeedbackScore] = useState<number | undefined>(_m?.feedbackScore);

  // ========================================================================
  // UI STATE
  // ========================================================================
  const [activeTab, setActiveTab] = useState('core');
  const [activeStep, setActiveStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [mfaModalOpened, setMfaModalOpened] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  // Template/Clone UI state
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [prefillPanelOpen, setPrefillPanelOpen] = useState(false);

  // ========================================================================
  // COLLECT / DISTRIBUTE — for clone, template, LLM prefill
  // ========================================================================

  const collectCurrentState = useCallback((): TaskFormState => ({
    name, description, externalRef, taskCategory, domain,
    taskType, tags,
    priority, dueDate: dueDate?.toISOString() ?? null, deadlineAt: deadlineAt?.toISOString() ?? null,
    estimatedDuration, statusReason, blockedReason,
    assignedTo, assignedTeam, assignedRole, assignmentMethod,
    watchers, collaborators, delegates,
    priorityScore, priorityReason, isCritical, requiresImmediateAttention,
    dependencies, relatedTasks, isParent, childTasks, completionStrategy,
    formSchema, formVersion, formTemplateId, contextData, variables,
    approvalRequired, approvalType, approvalPolicyId, approvers, approvalDecisions: approvalDecisions as any,
    dualControl: dualControl as any, requiresMfa, securityLevel,
    accessControlList, requiredPermissions, requiredRoles,
    escalationRules, escalationChain, slaDeadline: slaDeadline?.toISOString() ?? null,
    slaBufferMinutes, notificationChannels,
    integrationId, webhookUrl, callbackUrl, isSystemTask, isAutomated, automationScript,
    maxRetries, retryPolicy,
    uiComponent, icon, color, displayOrder, renderMode,
    allowComments, allowAttachments, allowDelegation, isArchived,
    complianceFrameworks, regulatoryRequirements,
    evidenceRequired, isConfidential, isPrivileged, retentionPeriodDays, legalHold,
    customFields, labels, locale, timezone,
    draftMode, feedbackScore, regulatoryAlignmentPct: undefined,
  }), [
    name, description, externalRef, taskCategory, domain, taskType, tags,
    priority, dueDate, deadlineAt, estimatedDuration, statusReason, blockedReason,
    assignedTo, assignedTeam, assignedRole, assignmentMethod, watchers, collaborators, delegates,
    priorityScore, priorityReason, isCritical, requiresImmediateAttention,
    dependencies, relatedTasks, isParent, childTasks, completionStrategy,
    formSchema, formVersion, formTemplateId, contextData, variables,
    approvalRequired, approvalType, approvalPolicyId, approvers, approvalDecisions,
    dualControl, requiresMfa, securityLevel, accessControlList, requiredPermissions, requiredRoles,
    escalationRules, escalationChain, slaDeadline, slaBufferMinutes, notificationChannels,
    integrationId, webhookUrl, callbackUrl, isSystemTask, isAutomated, automationScript,
    maxRetries, retryPolicy, uiComponent, icon, color, displayOrder, renderMode,
    allowComments, allowAttachments, allowDelegation, isArchived,
    complianceFrameworks, regulatoryRequirements,
    evidenceRequired, isConfidential, isPrivileged, retentionPeriodDays, legalHold,
    customFields, labels, locale, timezone, draftMode, feedbackScore,
  ]);

  /** Setters map for applyFormState — used by TaskTemplatePicker */
  const formSetters = useMemo<Partial<TaskFormSetters>>(() => ({
    name: setName, description: setDescription, externalRef: setExternalRef,
    taskCategory: setTaskCategory, domain: setDomain, taskType: setTaskType, tags: setTags,
    priority: setPriority,
    dueDate: ((v: string | null) => setDueDate(v ? new Date(v) : null)) as any,
    deadlineAt: ((v: string | null) => setDeadlineAt(v ? new Date(v) : null)) as any,
    estimatedDuration: setEstimatedDuration, statusReason: setStatusReason, blockedReason: setBlockedReason,
    assignedTo: setAssignedTo, assignedTeam: setAssignedTeam, assignedRole: setAssignedRole,
    assignmentMethod: setAssignmentMethod, watchers: setWatchers, collaborators: setCollaborators, delegates: setDelegates,
    priorityScore: setPriorityScore, priorityReason: setPriorityReason,
    isCritical: setIsCritical, requiresImmediateAttention: setRequiresImmediateAttention,
    dependencies: setDependencies, relatedTasks: setRelatedTasks, isParent: setIsParent,
    completionStrategy: setCompletionStrategy,
    formSchema: setFormSchema, formVersion: setFormVersion, formTemplateId: setFormTemplateId,
    contextData: setContextData, variables: setVariables,
    approvalRequired: setApprovalRequired, approvalType: setApprovalType,
    approvalPolicyId: setApprovalPolicyId, approvers: setApprovers,
    dualControl: setDualControl as any, requiresMfa: setRequiresMfa, securityLevel: setSecurityLevel,
    accessControlList: setAccessControlList, requiredPermissions: setRequiredPermissions, requiredRoles: setRequiredRoles,
    escalationRules: setEscalationRules, escalationChain: setEscalationChain,
    slaDeadline: ((v: string | null) => setSlaDeadline(v ? new Date(v) : null)) as any,
    slaBufferMinutes: setSlaBufferMinutes, notificationChannels: setNotificationChannels,
    integrationId: setIntegrationId, webhookUrl: setWebhookUrl, callbackUrl: setCallbackUrl,
    isSystemTask: setIsSystemTask, isAutomated: setIsAutomated, automationScript: setAutomationScript,
    maxRetries: setMaxRetries, retryPolicy: setRetryPolicy,
    uiComponent: setUiComponent, icon: setIcon, color: setColor,
    displayOrder: setDisplayOrder, renderMode: setRenderMode,
    allowComments: setAllowComments, allowAttachments: setAllowAttachments,
    allowDelegation: setAllowDelegation, isArchived: setIsArchived,
    complianceFrameworks: setComplianceFrameworks,
    regulatoryRequirements: setRegulatoryRequirements, evidenceRequired: setEvidenceRequired,
    isConfidential: setIsConfidential, isPrivileged: setIsPrivileged,
    retentionPeriodDays: setRetentionPeriodDays, legalHold: setLegalHold,
    customFields: setCustomFields, labels: setLabels, locale: setLocale, timezone: setTimezone,
    draftMode: setDraftMode, feedbackScore: setFeedbackScore,
  }), []);

  // ========================================================================
  // VALIDATION
  // ========================================================================

  const validateForm = useCallback((): boolean => {
    const errors: ValidationError[] = [];

    // Core validations
    if (!name?.trim()) {
      errors.push({
        field: 'name',
        message: 'Task name is required',
        severity: 'error',
      });
    }

    // workflowInstanceId is only required when submitting to the backend.
    // When browsing/editing templates, no instance is needed.
    if (!instanceId && !initialData?.instanceId) {
      errors.push({
        field: 'workflowInstanceId',
        message: 'Instance ID is required',
        severity: 'warning',
      });
    }

    // Priority validation
    if (isCritical && priority !== 'urgent' && priority !== 'critical') {
      errors.push({
        field: 'priority',
        message: 'Critical tasks must have urgent or critical priority',
        severity: 'warning',
      });
    }

    // Date validations
    if (dueDate && deadlineAt && dueDate > deadlineAt) {
      errors.push({
        field: 'dueDate',
        message: 'Due date cannot be after hard deadline',
        severity: 'error',
      });
    }

    // Approval validations
    if (approvalRequired && approvers.length === 0) {
      errors.push({
        field: 'approvers',
        message: 'At least one approver is required when approval is enabled',
        severity: 'error',
      });
    }

    // Dual control validations
    if (dualControl.required && (!requiredRoles || requiredRoles.length === 0)) {
      errors.push({
        field: 'dualControl',
        message: 'Dual control requires at least one role to be specified',
        severity: 'error',
      });
    }

    // Hierarchy validations
    if (isParent && childTasks.length === 0) {
      errors.push({
        field: 'childTasks',
        message: 'Parent tasks should have at least one child task',
        severity: 'warning',
      });
    }

    // SLA validations
    if (slaDeadline && slaBufferMinutes < 5) {
      errors.push({
        field: 'slaBufferMinutes',
        message: 'SLA buffer should be at least 5 minutes',
        severity: 'warning',
      });
    }

    // Compliance validations
    if (showLifeCriticalFeatures && complianceFrameworks.length === 0) {
      errors.push({
        field: 'complianceFrameworks',
        message: 'Life-critical tasks must specify compliance frameworks',
        severity: 'error',
      });
    }

    // JSON validations
    try {
      JSON.parse(formSchema);
    } catch {
      errors.push({
        field: 'formSchema',
        message: 'Form schema must be valid JSON',
        severity: 'error',
      });
    }

    try {
      JSON.parse(contextData);
    } catch {
      errors.push({
        field: 'contextData',
        message: 'Context data must be valid JSON',
        severity: 'error',
      });
    }

    setValidationErrors(errors);
    return errors.filter((e) => e.severity === 'error').length === 0;
  }, [
    name,
    instanceId,
    initialData,
    isCritical,
    priority,
    dueDate,
    deadlineAt,
    approvalRequired,
    approvers,
    dualControl,
    requiredRoles,
    isParent,
    childTasks,
    slaDeadline,
    slaBufferMinutes,
    showLifeCriticalFeatures,
    complianceFrameworks,
    formSchema,
    contextData,
  ]);

  // Auto-validate on changes
  useEffect(() => {
    if (name || description) {
      validateForm();
    }
  }, [name, description, validateForm]);

  // ========================================================================
  // FORM SUBMISSION
  // ========================================================================

  const handleSubmit = async () => {
    if (!validateForm()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix all validation errors before submitting',
        color: 'red',
        icon: <IconAlertCircle />,
      });
      return;
    }

    try {
      // Helper for safe JSON parsing
      const parseJsonSafe = (str: string, fallback: any = {}) => {
        try {
          return str && str !== '{}' ? JSON.parse(str) : fallback;
        } catch (error) {
          console.warn('JSON parse error:', error);
          return fallback;
        }
      };

      // Prepare task data
      const taskData: any = {
        workflowInstanceId: instanceId || initialData?.instanceId || '',
        name,
        description,
        taskType,
        priority,
        assignedTo,
        assignedTeam,
        dueDate: dueDate?.toISOString(),
        estimatedDuration,
        dependencies,
        approvalRequired,
        escalationRules,
        context: parseJsonSafe(contextData, {}),
        formSchema: parseJsonSafe(formSchema, {}),
        metadata: {
          ...parseJsonSafe(metadata, {}),
          // Core fields
          externalRef,
          taskCategory,
          domain,
          tags,
          // Lifecycle
          statusReason,
          blockedReason,
          // Assignment
          assignedRole,
          assignmentMethod,
          watchers,
          collaborators,
          delegates,
          // Priority
          priorityScore,
          priorityReason,
          isCritical,
          requiresImmediateAttention,
          // Hierarchy
          parentTaskId,
          isParent,
          childTaskIds: childTasks.map((t) => t.id),
          completionStrategy,
          // Approval & Security
          approvalType,
          approvalPolicyId,
          approvers,
          approvalDecisions,
          dualControl,
          requiresMfa,
          securityLevel,
          accessControlList,
          requiredPermissions,
          requiredRoles,
          // Escalation & SLA
          escalationChain,
          slaDeadline: slaDeadline?.toISOString(),
          slaBufferMinutes,
          notificationChannels,
          deadlineAt: deadlineAt?.toISOString(),
          // Integration
          integrationId,
          webhookUrl,
          callbackUrl,
          isSystemTask,
          isAutomated,
          automationScript,
          maxRetries,
          retryPolicy: parseJsonSafe(retryPolicy, {}),
          // UI & Display
          uiComponent,
          icon,
          color,
          displayOrder,
          renderMode,
          allowComments,
          allowAttachments,
          allowDelegation,
          isArchived,
          // Compliance & Audit
          complianceFrameworks,
          regulatoryRequirements,
          evidenceRequired,
          isConfidential,
          isPrivileged,
          retentionPeriodDays,
          legalHold,
          // Extensibility
          customFields: parseJsonSafe(customFields, {}),
          labels,
          locale,
          timezone,
          // Collaboration
          draftMode,
          feedbackScore,
          // Form
          formVersion,
          formTemplateId,
          variables: parseJsonSafe(variables, {}),
          relatedTasks,
        },
      };

      let result: Task | null = null;

      if (mode === 'create' || mode === 'clone') {
        result = await createTask(taskData);
      } else {
        result = await updateTask(initialData?.id || '', taskData);
      }

      if (result) {
        notifications.show({
          title: 'Success',
          message: `Task ${mode === 'create' ? 'created' : 'updated'} successfully`,
          color: 'green',
          icon: <IconCheck />,
        });

        onSuccess?.(result);
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      notifications.show({
        title: 'Error',
        message: `Failed to ${mode === 'create' ? 'create' : 'update'} task`,
        color: 'red',
        icon: <IconX />,
      });
    }
  };

  // ========================================================================
  // CHILD TASK MANAGEMENT
  // ========================================================================

  const addChildTask = () => {
    setChildTasks.append({
      id: `subtask_${Date.now()}`,
      name: '',
      taskType: 'user',
      priority: 'normal',
      estimatedDuration: undefined,
      sequenceOrder: childTasks.length + 1,
    });
  };

  const removeChildTask = (index: number) => {
    setChildTasks.remove(index);
  };

  const updateChildTask = (index: number, field: keyof SubTask, value: string | number | undefined) => {
    const current = childTasks[index];
    if (!current) return;
    setChildTasks.setItem(index, {
      ...current,
      [field]: value,
    });
  };

  // ========================================================================
  // ESCALATION RULES MANAGEMENT
  // ========================================================================

  const addEscalationRule = () => {
    setEscalationRules([
      ...escalationRules,
      {
        id: `rule_${Date.now()}`,
        trigger: 'overdue' as any,
        condition: {},
        action: 'notify' as any,
        escalateTo: '',
        delayMinutes: 60,
      },
    ]);
  };

  const removeEscalationRule = (index: number) => {
    setEscalationRules(escalationRules.filter((_: any, i) => i !== index));
  };

  const updateEscalationRule = (index: number, field: keyof EscalationRule, value: any) => {
    setEscalationRules(
      escalationRules.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)),
    );
  };

  // ========================================================================
  // COMPUTED VALUES
  // ========================================================================

  const totalEstimatedDuration = useMemo(() => {
    const base = estimatedDuration || 0;
    const childTotal = childTasks.reduce((sum, child) => sum + (child.estimatedDuration || 0), 0);
    return base + childTotal;
  }, [estimatedDuration, childTasks]);

  const criticalityLevel = useMemo(() => {
    if (isCritical) return 'critical';
    if (requiresImmediateAttention) return 'immediate';
    if (priority === 'urgent') return 'high';
    return 'normal';
  }, [isCritical, requiresImmediateAttention, priority]);

  const securityScore = useMemo(() => {
    let score = 0;
    if (requiresMfa) score += 25;
    if (dualControl.required) score += 25;
    if (accessControlList.length > 0) score += 15;
    if (requiredPermissions.length > 0) score += 15;
    if (securityLevel === 'secret') score += 20;
    return Math.min(score, 100);
  }, [requiresMfa, dualControl, accessControlList, requiredPermissions, securityLevel]);

  const completenessScore = useMemo(() => {
    let filled = 0;
    let total = 30; // Key fields

    if (name) filled++;
    if (description) filled++;
    if (assignedTo || assignedTeam) filled++;
    if (dueDate) filled++;
    if (estimatedDuration) filled++;
    if (tags.length > 0) filled++;
    if (dependencies.length > 0 || childTasks.length > 0) filled++;
    if (approvalRequired && approvers.length > 0) filled++;
    if (escalationRules.length > 0) filled++;
    if (slaDeadline) filled++;
    if (complianceFrameworks.length > 0) filled++;
    if (notificationChannels.length > 0) filled++;
    if (formSchema !== '{}') filled++;
    if (contextData !== '{}') filled++;
    if (metadata !== '{}') filled++;

    // Add more checks...
    filled += Math.min(5, watchers.length);
    filled += Math.min(5, collaborators.length);
    filled += Math.min(5, regulatoryRequirements.length);

    return Math.round((filled / total) * 100);
  }, [
    name,
    description,
    assignedTo,
    assignedTeam,
    dueDate,
    estimatedDuration,
    tags,
    dependencies,
    childTasks,
    approvalRequired,
    approvers,
    escalationRules,
    slaDeadline,
    complianceFrameworks,
    notificationChannels,
    formSchema,
    contextData,
    metadata,
    watchers,
    collaborators,
    regulatoryRequirements,
  ]);

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const renderValidationSummary = () => {
    if (validationErrors.length === 0) {
      return (
        <Alert color="green" icon={<IconCheck />} title="Validation Passed">
          All fields are valid. Ready to submit.
        </Alert>
      );
    }

    const errors = validationErrors.filter((e) => e.severity === 'error');
    const warnings = validationErrors.filter((e) => e.severity === 'warning');

    return (
      <Stack gap="xs">
        {errors.length > 0 && (
          <Alert
            color="red"
            icon={<IconX />}
            title={`${errors.length} Error${errors.length > 1 ? 's' : ''}`}
          >
            <Stack gap="xs">
              {errors.map((error, idx) => (
                <Text key={idx} size="sm">
                  <strong>{error.field}:</strong> {error.message}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert
            color="yellow"
            icon={<IconAlertTriangle />}
            title={`${warnings.length} Warning${warnings.length > 1 ? 's' : ''}`}
          >
            <Stack gap="xs">
              {warnings.map((warning, idx) => (
                <Text key={idx} size="sm">
                  <strong>{warning.field}:</strong> {warning.message}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  };

  const renderProgressIndicators = () => (
    <Grid>
      <Grid.Col span={4}>
        <Paper p="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconCheck size={16} />
            <Text size="sm" fw={500}>
              Completeness
            </Text>
          </Group>
          <RingProgress
            size={120}
            thickness={12}
            sections={[
              {
                value: completenessScore,
                color: completenessScore > 70 ? 'green' : completenessScore > 40 ? 'yellow' : 'red',
              },
            ]}
            label={
              <Text ta="center" fw={700} size="xl">
                {completenessScore}%
              </Text>
            }
          />
        </Paper>
      </Grid.Col>

      <Grid.Col span={4}>
        <Paper p="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconShield size={16} />
            <Text size="sm" fw={500}>
              Security
            </Text>
          </Group>
          <RingProgress
            size={120}
            thickness={12}
            sections={[
              {
                value: securityScore,
                color: securityScore > 70 ? 'green' : securityScore > 40 ? 'yellow' : 'red',
              },
            ]}
            label={
              <Text ta="center" fw={700} size="xl">
                {securityScore}%
              </Text>
            }
          />
        </Paper>
      </Grid.Col>

      <Grid.Col span={4}>
        <Paper p="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconFlame size={16} />
            <Text size="sm" fw={500}>
              Criticality
            </Text>
          </Group>
          <Badge
            size="xl"
            variant="filled"
            color={
              criticalityLevel === 'critical'
                ? 'red'
                : criticalityLevel === 'immediate'
                  ? 'orange'
                  : criticalityLevel === 'high'
                    ? 'yellow'
                    : 'blue'
            }
            fullWidth
            p="lg"
          >
            {criticalityLevel.toUpperCase()}
          </Badge>
        </Paper>
      </Grid.Col>
    </Grid>
  );

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  return (
    <Stack gap="lg">
      {/* Header */}
      <Paper p="xl" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={2}>
              {mode === 'create' ? '🎯 Create' : mode === 'edit' ? '✏️ Edit' : '📋 Clone'} Task
            </Title>
            <Text size="sm" c="dimmed" mt="xs">
              Comprehensive task management with 185+ fields • Life-critical validation
            </Text>
          </div>
          <Group>
            <Tooltip label="Show JSON preview">
              <ActionIcon
                variant={showPreview ? 'filled' : 'light'}
                onClick={() => setShowPreview(!showPreview)}
              >
                <IconCode size={18} />
              </ActionIcon>
            </Tooltip>
            <Badge leftSection={<IconDatabase size={14} />} size="lg">
              {mode.toUpperCase()}
            </Badge>
          </Group>
        </Group>

        {renderProgressIndicators()}
      </Paper>

      {/* Validation Summary */}
      {renderValidationSummary()}

      {/* JSON Preview */}
      <Collapse in={showPreview}>
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={500}>Task Data Preview</Text>
            <ActionIcon variant="light" size="sm">
              <IconCopy size={16} />
            </ActionIcon>
          </Group>
          <ScrollArea h={300}>
            <Code block>
              {JSON.stringify(
                {
                  name,
                  description,
                  taskType,
                  priority,
                  assignedTo,
                  dueDate: dueDate?.toISOString(),
                  isCritical,
                  approvalRequired,
                  dualControlRequired: dualControl.required,
                  childTasksCount: childTasks.length,
                  escalationRulesCount: escalationRules.length,
                },
                null,
                2,
              )}
            </Code>
          </ScrollArea>
        </Paper>
      </Collapse>

      {/* Template Toolbar */}
      {templateToolbarEnabled && (
        <Group justify="flex-end" mb="xs">
          <TaskFormToolbar
            collectCurrentState={collectCurrentState}
            onClone={onClone}
            onSaveTemplate={() => setSaveTemplateOpen(true)}
            onLoadTemplate={() => setTemplatePickerOpen(true)}
            onLlmPrefill={onLlmPrefill ?? (() => setPrefillPanelOpen(true))}
          />
        </Group>
      )}

      {/* Main Form Tabs */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'core')}>
        <Tabs.List grow>
          <Tabs.Tab value="core" leftSection={<IconTarget size={16} />}>
            Core
          </Tabs.Tab>
          <Tabs.Tab value="hierarchy" leftSection={<IconHierarchy size={16} />}>
            Hierarchy
          </Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<IconShieldCheck size={16} />}>
            Security
          </Tabs.Tab>
          <Tabs.Tab value="escalation" leftSection={<IconTrendingUp size={16} />}>
            Escalation
          </Tabs.Tab>
          <Tabs.Tab value="compliance" leftSection={<IconGavel size={16} />}>
            Compliance
          </Tabs.Tab>
          <Tabs.Tab value="advanced" leftSection={<IconCode size={16} />}>
            Advanced
          </Tabs.Tab>
        </Tabs.List>

        {/* TAB 1: CORE */}
        <Tabs.Panel value="core" pt="lg">
          <Stack gap="lg">
            <Card withBorder>
              <Title order={4} mb="md">
                ✨ Basic Information
              </Title>
              <Stack gap="md">
                <TextInput
                  label="Task Name"
                  placeholder="e.g., Review patient admission documents"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  leftSection={<IconListCheck size={16} />}
                  error={validationErrors.find((e) => e.field === 'name')?.message}
                />

                <Textarea
                  label="Description"
                  placeholder="Detailed description of what needs to be done..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  minRows={3}
                  maxRows={6}
                />

                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="External Reference"
                      placeholder="e.g., JIRA-12345"
                      value={externalRef}
                      onChange={(e) => setExternalRef(e.target.value)}
                      leftSection={<IconLink size={16} />}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Domain"
                      placeholder="e.g., healthcare, finance"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      leftSection={<IconTag size={16} />}
                    />
                  </Grid.Col>
                </Grid>

                <TagsInput
                  label="Tags"
                  placeholder="Add tags (press Enter)"
                  value={tags}
                  onChange={setTags}
                  leftSection={<IconTag size={16} />}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                📊 Classification
              </Title>
              <Stack gap="md">
                <Select
                  label="Task Type"
                  value={taskType}
                  onChange={(val) => val && setTaskType(val)}
                  data={[
                    { value: 'user', label: '👤 User Task - Requires human action' },
                    { value: 'system', label: '🤖 System Task - Automated processing' },
                    { value: 'approval', label: '✅ Approval - Requires authorization' },
                    { value: 'review', label: '👁️ Review - Content review' },
                    { value: 'manual', label: '✋ Manual - Hands-on work' },
                    { value: 'validation', label: '🔍 Validation - Data verification' },
                  ]}
                  required
                />

                <Select
                  label="Task Category"
                  value={taskCategory}
                  onChange={(val) => val && setTaskCategory(val)}
                  data={[
                    { value: 'registration', label: 'Registration' },
                    { value: 'coding', label: 'Coding' },
                    { value: 'billing', label: 'Billing' },
                    { value: 'clinical', label: 'Clinical' },
                    { value: 'administrative', label: 'Administrative' },
                    { value: 'compliance', label: 'Compliance' },
                  ]}
                  placeholder="Select category"
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                ⏰ Priority & Scheduling
              </Title>
              <Stack gap="md">
                <Select
                  label="Priority"
                  value={priority}
                  onChange={(val) => val && setPriority(val)}
                  data={[
                    { value: 'low', label: '🟢 Low' },
                    { value: 'normal', label: '🔵 Normal' },
                    { value: 'high', label: '🟡 High' },
                    { value: 'urgent', label: '🟠 Urgent' },
                    { value: 'critical', label: '🔴 Critical' },
                  ]}
                  required
                />

                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Priority Score (0-100)
                  </Text>
                  <Slider
                    value={priorityScore}
                    onChange={setPriorityScore}
                    min={0}
                    max={100}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' },
                      { value: 75, label: '75' },
                      { value: 100, label: '100' },
                    ]}
                    color={
                      priorityScore > 75
                        ? 'red'
                        : priorityScore > 50
                          ? 'orange'
                          : priorityScore > 25
                            ? 'yellow'
                            : 'blue'
                    }
                  />
                </div>

                <Textarea
                  label="Priority Reason"
                  placeholder="Explain why this priority level..."
                  value={priorityReason}
                  onChange={(e) => setPriorityReason(e.target.value)}
                  minRows={2}
                />

                <Group grow>
                  <Switch
                    label="🔥 Life-Critical Task"
                    description="Patient safety or life-critical operation"
                    checked={isCritical}
                    onChange={(e) => setIsCritical(e.currentTarget.checked)}
                  />
                  <Switch
                    label="⚡ Requires Immediate Attention"
                    description="Must be addressed immediately"
                    checked={requiresImmediateAttention}
                    onChange={(e) => setRequiresImmediateAttention(e.currentTarget.checked)}
                  />
                </Group>

                <Grid>
                  <Grid.Col span={6}>
                    <DateTimePicker
                      label="Due Date (Soft)"
                      placeholder="Select due date"
                      value={dueDate}
                      onChange={(value: string | null) => setDueDate(value ? new Date(value) : null)}
                      leftSection={<IconCalendar size={16} />}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <DateTimePicker
                      label="Hard Deadline"
                      placeholder="Must complete by"
                      value={deadlineAt}
                      onChange={(value: string | null) => setDeadlineAt(value ? new Date(value) : null)}
                      leftSection={<IconAlarm size={16} />}
                    />
                  </Grid.Col>
                </Grid>

                <NumberInput
                  label="Estimated Duration (minutes)"
                  placeholder="How long will this take?"
                  value={estimatedDuration}
                  onChange={(val) => setEstimatedDuration(val as number)}
                  leftSection={<IconClock size={16} />}
                  min={0}
                />

                {totalEstimatedDuration > 0 && (
                  <Alert color="blue" icon={<IconInfoCircle />}>
                    <Text size="sm">
                      <strong>Total estimated time:</strong>{' '}
                      {Math.floor(totalEstimatedDuration / 60)}h {totalEstimatedDuration % 60}m
                      {childTasks.length > 0 && ` (including ${childTasks.length} subtasks)`}
                    </Text>
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                👥 Assignment
              </Title>
              <Stack gap="md">
                <Select
                  label="Assignment Method"
                  value={assignmentMethod}
                  onChange={(val) => val && setAssignmentMethod(val as any)}
                  data={[
                    { value: 'manual', label: '✋ Manual Assignment' },
                    { value: 'auto', label: '🤖 Auto-Assignment (Round Robin)' },
                    { value: 'escalated', label: '⬆️ Escalated Assignment' },
                  ]}
                />

                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Assigned To (User ID)"
                      placeholder="user@example.com"
                      value={assignedTo}
                      onChange={(e) => setAssignedTo(e.target.value)}
                      leftSection={<IconUser size={16} />}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Assigned Team"
                      placeholder="approval-team"
                      value={assignedTeam}
                      onChange={(e) => setAssignedTeam(e.target.value)}
                      leftSection={<IconUsers size={16} />}
                    />
                  </Grid.Col>
                </Grid>

                <TextInput
                  label="Required Role"
                  placeholder="e.g., registered_nurse"
                  value={assignedRole}
                  onChange={(e) => setAssignedRole(e.target.value)}
                  leftSection={<IconUserCheck size={16} />}
                />

                <TagsInput
                  label="Watchers"
                  description="Users who will be notified of updates"
                  placeholder="Add watcher (press Enter)"
                  value={watchers}
                  onChange={setWatchers}
                  leftSection={<IconEye size={16} />}
                />

                <TagsInput
                  label="Collaborators"
                  description="Users who can collaborate on this task"
                  placeholder="Add collaborator (press Enter)"
                  value={collaborators}
                  onChange={setCollaborators}
                  leftSection={<IconUsers size={16} />}
                />

                <TagsInput
                  label="Delegates"
                  description="Users with delegate access"
                  placeholder="Add delegate (press Enter)"
                  value={delegates}
                  onChange={setDelegates}
                  leftSection={<IconUsers size={16} />}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                🔗 Dependencies
              </Title>
              <Stack gap="md">
                <TagsInput
                  label="Task Dependencies"
                  description="Tasks that must complete before this one starts"
                  placeholder="Add task ID (press Enter)"
                  value={dependencies}
                  onChange={setDependencies}
                  leftSection={<IconArrowsSplit size={16} />}
                />

                <TagsInput
                  label="Related Tasks"
                  description="Related but not blocking tasks"
                  placeholder="Add task ID (press Enter)"
                  value={relatedTasks}
                  onChange={setRelatedTasks}
                  leftSection={<IconLink size={16} />}
                />

                {dependencies.length > 0 && (
                  <Alert color="blue" icon={<IconInfoCircle />}>
                    This task depends on {dependencies.length} other task
                    {dependencies.length > 1 ? 's' : ''} to complete first.
                  </Alert>
                )}
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* TAB 2: HIERARCHY */}
        <Tabs.Panel value="hierarchy" pt="lg">
          <Stack gap="lg">
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>🏗️ Task Hierarchy</Title>
                  <Text size="sm" c="dimmed">
                    Create parent-child task relationships for complex workflows
                  </Text>
                </div>
                <Switch
                  label="This is a parent task"
                  checked={isParent}
                  onChange={(e) => setIsParent(e.currentTarget.checked)}
                  size="lg"
                />
              </Group>

              {parentTaskId && (
                <Alert color="blue" icon={<IconHierarchy />} mb="md">
                  <Group>
                    <Text size="sm">
                      This is a <strong>child task</strong> of parent: <Code>{parentTaskId}</Code>
                    </Text>
                    <ActionIcon variant="light" size="sm">
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  </Group>
                </Alert>
              )}

              {isParent && (
                <Stack gap="md">
                  <Select
                    label="Completion Strategy"
                    description="How should the parent task be marked complete?"
                    value={completionStrategy}
                    onChange={(val) => val && setCompletionStrategy(val)}
                    data={[
                      {
                        value: 'all_children_must_complete',
                        label: '✅ All children must complete',
                      },
                      {
                        value: 'any_child_completes',
                        label: '🎯 Any child completes (first wins)',
                      },
                      {
                        value: 'percentage_threshold',
                        label: '📊 Percentage threshold (e.g., 80%)',
                      },
                      { value: 'weighted_completion', label: '⚖️ Weighted completion' },
                      { value: 'custom_logic', label: '🔧 Custom logic' },
                    ]}
                  />

                  <Divider label="Child Tasks" labelPosition="center" />

                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      {childTasks.length} child task{childTasks.length !== 1 ? 's' : ''}
                    </Text>
                    <Button
                      leftSection={<IconPlus size={16} />}
                      onClick={addChildTask}
                      variant="light"
                      size="sm"
                    >
                      Add Child Task
                    </Button>
                  </Group>

                  {childTasks.length > 0 && (
                    <Stack gap="sm">
                      {childTasks.map((child, index) => (
                        <Paper key={child.id} p="md" withBorder>
                          <Group justify="space-between" mb="xs">
                            <Badge>#{child.sequenceOrder}</Badge>
                            <ActionIcon
                              color="red"
                              variant="light"
                              onClick={() => removeChildTask(index)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                          <Grid>
                            <Grid.Col span={6}>
                              <TextInput
                                placeholder="Child task name"
                                value={child.name}
                                onChange={(e) => updateChildTask(index, 'name', e.target.value)}
                                size="sm"
                              />
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Select
                                placeholder="Type"
                                value={child.taskType}
                                onChange={(val) => val && updateChildTask(index, 'taskType', val)}
                                data={[
                                  { value: 'user', label: 'User' },
                                  { value: 'system', label: 'System' },
                                  { value: 'approval', label: 'Approval' },
                                ]}
                                size="sm"
                              />
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Select
                                placeholder="Priority"
                                value={child.priority}
                                onChange={(val) => val && updateChildTask(index, 'priority', val)}
                                data={[
                                  { value: 'low', label: 'Low' },
                                  { value: 'normal', label: 'Normal' },
                                  { value: 'high', label: 'High' },
                                  { value: 'urgent', label: 'Urgent' },
                                ]}
                                size="sm"
                              />
                            </Grid.Col>
                            <Grid.Col span={6}>
                              <TextInput
                                placeholder="Assigned to"
                                value={child.assignedTo || ''}
                                onChange={(e) =>
                                  updateChildTask(index, 'assignedTo', e.target.value)
                                }
                                size="sm"
                              />
                            </Grid.Col>
                            <Grid.Col span={6}>
                              <NumberInput
                                placeholder="Duration (min)"
                                value={child.estimatedDuration}
                                onChange={(val) => updateChildTask(index, 'estimatedDuration', val)}
                                size="sm"
                                min={0}
                              />
                            </Grid.Col>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
            </Card>

            {isParent && childTasks.length > 0 && (
              <Card withBorder>
                <Title order={5} mb="md">
                  📊 Hierarchy Visualization
                </Title>
                <Paper p="md" bg="gray.0">
                  <Stack gap="xs">
                    <Group>
                      <IconHierarchy size={20} />
                      <Text fw={700}>{name || 'Parent Task'}</Text>
                      <Badge size="xs">{completionStrategy.replace(/_/g, ' ')}</Badge>
                    </Group>
                    <Stack gap={4} pl="xl">
                      {childTasks.map((child, idx) => (
                        <Group key={child.id} gap="xs">
                          <IconChevronRight size={14} />
                          <Text size="sm">{child.name || `Child Task ${idx + 1}`}</Text>
                          <Badge
                            size="xs"
                            variant="dot"
                            color={
                              child.priority === 'urgent'
                                ? 'red'
                                : child.priority === 'high'
                                  ? 'orange'
                                  : child.priority === 'normal'
                                    ? 'blue'
                                    : 'gray'
                            }
                          >
                            {child.priority}
                          </Badge>
                        </Group>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        {/* TAB 3: SECURITY */}
        <Tabs.Panel value="security" pt="lg">
          <Stack gap="lg">
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>🔒 Approval & Authorization</Title>
                  <Text size="sm" c="dimmed">
                    Configure approval workflows and dual control
                  </Text>
                </div>
                <Badge size="lg" leftSection={<IconShield size={14} />}>
                  Security Score: {securityScore}%
                </Badge>
              </Group>

              <Stack gap="md">
                <Switch
                  label="Approval Required"
                  description="This task requires explicit approval before completion"
                  checked={approvalRequired}
                  onChange={(e) => setApprovalRequired(e.currentTarget.checked)}
                  size="lg"
                />

                {approvalRequired && (
                  <Stack gap="md" pl="md">
                    <Select
                      label="Approval Type"
                      value={approvalType}
                      onChange={(val) => val && setApprovalType(val)}
                      data={[
                        { value: 'manager', label: 'Manager Approval' },
                        { value: 'compliance', label: 'Compliance Approval' },
                        { value: 'medical', label: 'Medical Approval' },
                        { value: 'financial', label: 'Financial Approval' },
                        { value: 'legal', label: 'Legal Approval' },
                        { value: 'multi_level', label: 'Multi-Level Approval' },
                      ]}
                    />

                    <TextInput
                      label="Approval Policy ID"
                      placeholder="policy-001"
                      value={approvalPolicyId}
                      onChange={(e) => setApprovalPolicyId(e.target.value)}
                      description="Links to data-driven approval policy"
                    />

                    <TagsInput
                      label="Approvers"
                      description="User IDs who can approve this task"
                      placeholder="Add approver (press Enter)"
                      value={approvers}
                      onChange={setApprovers}
                      error={validationErrors.find((e) => e.field === 'approvers')?.message}
                    />
                  </Stack>
                )}

                <Divider />

                <Switch
                  label="🔐 Dual Control (Two-Person Rule)"
                  description="Requires two different users to sign off"
                  checked={dualControl.required}
                  onChange={(e) =>
                    setDualControl({ ...dualControl, required: e.currentTarget.checked })
                  }
                  size="lg"
                />

                {dualControl.required && (
                  <Stack gap="md" pl="md">
                    <Alert color="orange" icon={<IconShieldCheck />}>
                      <Text size="sm">
                        <strong>Dual Control Enabled:</strong> Two users with different roles must
                        sign. First signer blocks the task, second signer completes it.
                      </Text>
                    </Alert>

                    <TagsInput
                      label="Required Roles"
                      description="At least one role from this list must sign"
                      placeholder="Add role (press Enter)"
                      value={requiredRoles}
                      onChange={setRequiredRoles}
                      error={validationErrors.find((e) => e.field === 'dualControl')?.message}
                    />

                    {dualControl.firstSigner && (
                      <Paper p="md" withBorder bg="blue.0">
                        <Stack gap="xs">
                          <Group>
                            <IconUserCheck size={16} />
                            <Text size="sm" fw={500}>
                              First Signature
                            </Text>
                          </Group>
                          <Text size="sm">
                            Signed by:{' '}
                            <Code>{dualControl.firstSignerName || dualControl.firstSigner}</Code>
                          </Text>
                          <Text size="sm">
                            At: <Code>{dualControl.firstSignedAt}</Code>
                          </Text>
                          <Text size="sm">
                            MFA Verified: {dualControl.firstSignerMfaVerified ? '✅' : '❌'}
                          </Text>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                )}

                <Divider />

                <Switch
                  label="🔑 MFA Required"
                  description="Multi-factor authentication required for completion"
                  checked={requiresMfa}
                  onChange={(e) => setRequiresMfa(e.currentTarget.checked)}
                  size="lg"
                />

                {requiresMfa && (
                  <Alert color="blue" icon={<IconFingerprint />}>
                    Users must verify their identity with MFA before completing this task.
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                🛡️ Security Controls
              </Title>
              <Stack gap="md">
                <Select
                  label="Security Level"
                  value={securityLevel}
                  onChange={(val) => val && setSecurityLevel(val)}
                  data={[
                    { value: 'public', label: '🌍 Public' },
                    { value: 'internal', label: '🏢 Internal' },
                    { value: 'confidential', label: '🔒 Confidential' },
                    { value: 'secret', label: '🔐 Secret' },
                    { value: 'top_secret', label: '🚨 Top Secret' },
                  ]}
                />

                <TagsInput
                  label="Access Control List"
                  description="Explicit user IDs who can access this task"
                  placeholder="Add user ID (press Enter)"
                  value={accessControlList}
                  onChange={setAccessControlList}
                  leftSection={<IconLock size={16} />}
                />

                <TagsInput
                  label="Required Permissions"
                  description="Permissions needed to access this task"
                  placeholder="Add permission (press Enter)"
                  value={requiredPermissions}
                  onChange={setRequiredPermissions}
                  leftSection={<IconShieldCheck size={16} />}
                />

                <TagsInput
                  label="Required Roles"
                  description="Roles needed to access this task"
                  placeholder="Add role (press Enter)"
                  value={requiredRoles}
                  onChange={setRequiredRoles}
                  leftSection={<IconUserCheck size={16} />}
                />

                <Group grow>
                  <Switch
                    label="Confidential"
                    description="Contains sensitive information"
                    checked={isConfidential}
                    onChange={(e) => setIsConfidential(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Privileged"
                    description="Attorney-client privilege"
                    checked={isPrivileged}
                    onChange={(e) => setIsPrivileged(e.currentTarget.checked)}
                  />
                </Group>
              </Stack>
            </Card>

            {approvalDecisions.length > 0 && (
              <Card withBorder>
                <Title order={5} mb="md">
                  📋 Approval History
                </Title>
                <Timeline active={approvalDecisions.length}>
                  {approvalDecisions.map((decision, idx) => (
                    <Timeline.Item
                      key={idx}
                      bullet={
                        decision.decision === 'approved' ? (
                          <IconCheck size={12} />
                        ) : decision.decision === 'rejected' ? (
                          <IconX size={12} />
                        ) : (
                          <IconAlertTriangle size={12} />
                        )
                      }
                      title={decision.approverName}
                    >
                      <Text c="dimmed" size="sm">
                        {decision.decision.toUpperCase()}
                      </Text>
                      {decision.comments && (
                        <Text size="sm" mt={4}>
                          {decision.comments}
                        </Text>
                      )}
                      <Text size="xs" mt={4}>
                        {new Date(decision.decidedAt).toLocaleString()}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        {/* TAB 4: ESCALATION */}
        <Tabs.Panel value="escalation" pt="lg">
          <Stack gap="lg">
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>📈 Escalation Rules</Title>
                  <Text size="sm" c="dimmed">
                    Automatic escalation based on SLA and conditions
                  </Text>
                </div>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={addEscalationRule}
                  variant="light"
                >
                  Add Rule
                </Button>
              </Group>

              {escalationRules.length > 0 ? (
                <Stack gap="md">
                  {escalationRules.map((rule, index) => (
                    <Paper key={rule.id} p="md" withBorder>
                      <Group justify="space-between" mb="md">
                        <Badge>Rule #{index + 1}</Badge>
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => removeEscalationRule(index)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>

                      <Grid>
                        <Grid.Col span={6}>
                          <Select
                            label="Trigger"
                            value={rule.trigger}
                            onChange={(val) => val && updateEscalationRule(index, 'trigger', val)}
                            data={[
                              { value: 'overdue', label: '⏰ Overdue' },
                              { value: 'stale', label: '💤 Stale (No Activity)' },
                              { value: 'priority', label: '🔥 Priority Threshold' },
                              { value: 'manual', label: '✋ Manual Trigger' },
                            ]}
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Select
                            label="Action"
                            value={rule.action}
                            onChange={(val) => val && updateEscalationRule(index, 'action', val)}
                            data={[
                              { value: 'notify', label: '📧 Notify' },
                              { value: 'reassign', label: '👤 Reassign' },
                              { value: 'escalate_priority', label: '⬆️ Escalate Priority' },
                              { value: 'create_incident', label: '🚨 Create Incident' },
                            ]}
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <TextInput
                            label="Escalate To"
                            placeholder="user@example.com"
                            value={rule.escalateTo || ''}
                            onChange={(e) =>
                              updateEscalationRule(index, 'escalateTo', e.target.value)
                            }
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="Delay (minutes)"
                            placeholder="60"
                            value={rule.delayMinutes}
                            onChange={(val) => updateEscalationRule(index, 'delayMinutes', val)}
                            min={0}
                          />
                        </Grid.Col>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Paper p="xl" ta="center" bg="gray.0">
                  <IconTrendingUp size={48} stroke={1} opacity={0.3} />
                  <Text size="sm" c="dimmed" mt="md">
                    No escalation rules configured. Add rules to enable automatic escalation.
                  </Text>
                </Paper>
              )}
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                ⏱️ SLA Management
              </Title>
              <Stack gap="md">
                <DateTimePicker
                  label="SLA Deadline"
                  description="Service Level Agreement deadline"
                  placeholder="Select SLA deadline"
                  value={slaDeadline}
                  onChange={(value: string | null) => setSlaDeadline(value ? new Date(value) : null)}
                  leftSection={<IconAlarm size={16} />}
                />

                <NumberInput
                  label="SLA Buffer (minutes)"
                  description="Warning before SLA breach"
                  placeholder="30"
                  value={slaBufferMinutes}
                  onChange={(val) => setSlaBufferMinutes(val as number)}
                  min={5}
                  max={480}
                />

                {slaDeadline && (
                  <Alert color="blue" icon={<IconClock />}>
                    <Text size="sm">
                      <strong>SLA Warning:</strong> You will be notified {slaBufferMinutes} minutes
                      before the deadline.
                    </Text>
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                🔔 Notification Settings
              </Title>
              <Stack gap="md">
                <TagsInput
                  label="Escalation Chain"
                  description="Users in escalation chain (in order)"
                  placeholder="Add user ID (press Enter)"
                  value={escalationChain}
                  onChange={setEscalationChain}
                  leftSection={<IconUsers size={16} />}
                />

                <MultiSelect
                  label="Notification Channels"
                  description="How to notify on escalation"
                  value={notificationChannels}
                  onChange={setNotificationChannels}
                  data={[
                    { value: 'email', label: '📧 Email' },
                    { value: 'sms', label: '📱 SMS' },
                    { value: 'push', label: '🔔 Push Notification' },
                    { value: 'slack', label: '💬 Slack' },
                    { value: 'webhook', label: '🔗 Webhook' },
                  ]}
                />
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* TAB 5: COMPLIANCE */}
        <Tabs.Panel value="compliance" pt="lg">
          <Stack gap="lg">
            <Card withBorder>
              <Title order={4} mb="md">
                ⚖️ Compliance & Regulatory
              </Title>
              <Stack gap="md">
                <MultiSelect
                  label="Compliance Frameworks"
                  description="Applicable compliance frameworks"
                  value={complianceFrameworks}
                  onChange={setComplianceFrameworks}
                  data={[
                    { value: 'HIPAA', label: '🏥 HIPAA - Healthcare Privacy' },
                    { value: 'SOX', label: '💼 SOX - Financial Controls' },
                    { value: 'GDPR', label: '🇪🇺 GDPR - Data Protection' },
                    { value: 'FDA_21_CFR_PART_11', label: '💊 FDA 21 CFR Part 11' },
                    { value: 'ISO_27001', label: '🔒 ISO 27001 - Information Security' },
                    { value: 'PCI_DSS', label: '💳 PCI DSS - Payment Card' },
                    { value: 'FINRA', label: '📊 FINRA - Financial Regulation' },
                  ]}
                  error={validationErrors.find((e) => e.field === 'complianceFrameworks')?.message}
                />

                {/* T3 QAPI score field removed — belongs in QAPI dashboard only (DRR-114) */}

                <TagsInput
                  label="Regulatory Requirements"
                  description="Specific regulatory requirements"
                  placeholder="Add requirement (press Enter)"
                  value={regulatoryRequirements}
                  onChange={setRegulatoryRequirements}
                  leftSection={<IconGavel size={16} />}
                />

                <Switch
                  label="📎 Evidence Collection Required"
                  description="Requires evidence/documentation for audit"
                  checked={evidenceRequired}
                  onChange={(e) => setEvidenceRequired(e.currentTarget.checked)}
                />

                {evidenceRequired && (
                  <Alert color="orange" icon={<IconFileCheck />}>
                    Evidence must be collected and attached before task completion.
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                🗄️ Data Retention & Legal
              </Title>
              <Stack gap="md">
                <NumberInput
                  label="Retention Period (days)"
                  description="How long to retain this task data"
                  placeholder="2555"
                  value={retentionPeriodDays}
                  onChange={(val) => setRetentionPeriodDays(val as number)}
                  min={1}
                  leftSection={<IconHistory size={16} />}
                />

                <Switch
                  label="⚠️ Legal Hold"
                  description="Prevent deletion during litigation"
                  checked={legalHold}
                  onChange={(e) => setLegalHold(e.currentTarget.checked)}
                />

                {legalHold && (
                  <Alert color="red" icon={<IconLock />}>
                    <strong>Legal Hold Active:</strong> This task cannot be deleted and must be
                    preserved for legal proceedings.
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={5} mb="md">
                📊 Audit Trail Preview
              </Title>
              <Paper p="md" bg="gray.0">
                <Stack gap="xs">
                  <Group>
                    <IconHistory size={16} />
                    <Text size="sm" fw={500}>
                      Audit Events (Preview)
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    • TASK_CREATED - {new Date().toISOString()}
                  </Text>
                  {approvalRequired && (
                    <Text size="xs" c="dimmed">
                      • APPROVAL_REQUIRED_FLAG_SET
                    </Text>
                  )}
                  {dualControl.required && (
                    <Text size="xs" c="dimmed">
                      • DUAL_CONTROL_ENABLED
                    </Text>
                  )}
                  {isCritical && (
                    <Text size="xs" c="dimmed">
                      • CRITICAL_TASK_FLAG_SET
                    </Text>
                  )}
                </Stack>
              </Paper>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* TAB 6: ADVANCED */}
        <Tabs.Panel value="advanced" pt="lg">
          <Stack gap="lg">
            <Card withBorder>
              <Title order={4} mb="md">
                🔗 Integration & Webhooks
              </Title>
              <Stack gap="md">
                <TextInput
                  label="Integration ID"
                  placeholder="integration-123"
                  value={integrationId}
                  onChange={(e) => setIntegrationId(e.target.value)}
                  leftSection={<IconLink size={16} />}
                />

                <TextInput
                  label="Webhook URL"
                  placeholder="https://api.example.com/webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  leftSection={<IconWebhook size={16} />}
                />

                <TextInput
                  label="Callback URL"
                  placeholder="https://api.example.com/callback"
                  value={callbackUrl}
                  onChange={(e) => setCallbackUrl(e.target.value)}
                  leftSection={<IconExternalLink size={16} />}
                />

                <Group grow>
                  <Switch
                    label="System Task"
                    description="System-generated task"
                    checked={isSystemTask}
                    onChange={(e) => setIsSystemTask(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Automated"
                    description="Auto-execute with script"
                    checked={isAutomated}
                    onChange={(e) => setIsAutomated(e.currentTarget.checked)}
                  />
                </Group>

                {isAutomated && (
                  <Textarea
                    label="Automation Script"
                    placeholder="// JavaScript automation script"
                    value={automationScript}
                    onChange={(e) => setAutomationScript(e.target.value)}
                    minRows={5}
                    maxRows={10}
                    styles={{ input: { fontFamily: 'monospace' } }}
                  />
                )}

                <NumberInput
                  label="Max Retries"
                  description="Maximum retry attempts on failure"
                  value={maxRetries}
                  onChange={(val) => setMaxRetries(val as number)}
                  min={0}
                  max={10}
                />

                <JsonInput
                  label="Retry Policy (JSON)"
                  placeholder='{"backoff": "exponential", "maxBackoffMs": 60000}'
                  value={retryPolicy}
                  onChange={setRetryPolicy}
                  validationError="Invalid JSON"
                  formatOnBlur
                  autosize
                  minRows={3}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                🎨 UI & Display
              </Title>
              <Stack gap="md">
                <TextInput
                  label="UI Component"
                  placeholder="CustomTaskForm"
                  value={uiComponent}
                  onChange={(e) => setUiComponent(e.target.value)}
                  description="Custom React component to render this task"
                />

                <Select
                  label="Render Mode"
                  value={renderMode}
                  onChange={(val) => val && setRenderMode(val)}
                  data={[
                    { value: 'form', label: '📝 Form' },
                    { value: 'modal', label: '🪟 Modal' },
                    { value: 'inline', label: '📄 Inline' },
                    { value: 'custom', label: '🔧 Custom' },
                  ]}
                />

                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Icon"
                      placeholder="icon-name or emoji"
                      value={icon}
                      onChange={(e) => setIcon(e.target.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <ColorInput
                      label="Color"
                      value={color}
                      onChange={setColor}
                      format="hex"
                      swatches={['#228be6', '#40c057', '#fab005', '#fa5252', '#be4bdb']}
                    />
                  </Grid.Col>
                </Grid>

                <NumberInput
                  label="Display Order"
                  placeholder="0"
                  value={displayOrder}
                  onChange={(val) => setDisplayOrder(val as number)}
                  description="Order in task lists"
                />

                <Group grow>
                  <Switch
                    label="Allow Comments"
                    checked={allowComments}
                    onChange={(e) => setAllowComments(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Allow Attachments"
                    checked={allowAttachments}
                    onChange={(e) => setAllowAttachments(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Allow Delegation"
                    checked={allowDelegation}
                    onChange={(e) => setAllowDelegation(e.currentTarget.checked)}
                  />
                </Group>

                <Switch
                  label="Archived"
                  description="Hide from active task lists"
                  checked={isArchived}
                  onChange={(e) => setIsArchived(e.currentTarget.checked)}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                💾 Form Schema & Data
              </Title>
              <Stack gap="md">
                <TextInput
                  label="Form Version"
                  placeholder="1.0.0"
                  value={formVersion}
                  onChange={(e) => setFormVersion(e.target.value)}
                />

                <TextInput
                  label="Form Template ID"
                  placeholder="template-001"
                  value={formTemplateId}
                  onChange={(e) => setFormTemplateId(e.target.value)}
                />

                <JsonInput
                  label="Form Schema (JSON Schema)"
                  placeholder='{"type": "object", "properties": {...}}'
                  value={formSchema}
                  onChange={setFormSchema}
                  validationError="Invalid JSON Schema"
                  formatOnBlur
                  autosize
                  minRows={5}
                  maxRows={15}
                />

                <JsonInput
                  label="Context Data (JSON)"
                  placeholder='{"patientId": "12345", "encounterId": "67890"}'
                  value={contextData}
                  onChange={setContextData}
                  validationError="Invalid JSON"
                  formatOnBlur
                  autosize
                  minRows={5}
                  maxRows={15}
                />

                <JsonInput
                  label="Variables (JSON)"
                  placeholder='{"key": "value"}'
                  value={variables}
                  onChange={setVariables}
                  validationError="Invalid JSON"
                  formatOnBlur
                  autosize
                  minRows={3}
                  maxRows={10}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                🌐 Localization & Metadata
              </Title>
              <Stack gap="md">
                <Grid>
                  <Grid.Col span={6}>
                    <Select
                      label="Locale"
                      value={locale}
                      onChange={(val) => val && setLocale(val)}
                      data={[
                        { value: 'en-US', label: '🇺🇸 English (US)' },
                        { value: 'en-GB', label: '🇬🇧 English (UK)' },
                        { value: 'es-ES', label: '🇪🇸 Spanish' },
                        { value: 'fr-FR', label: '🇫🇷 French' },
                        { value: 'de-DE', label: '🇩🇪 German' },
                        { value: 'ja-JP', label: '🇯🇵 Japanese' },
                        { value: 'zh-CN', label: '🇨🇳 Chinese' },
                      ]}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select
                      label="Timezone"
                      value={timezone}
                      onChange={(val) => val && setTimezone(val)}
                      data={[
                        { value: 'America/New_York', label: 'Eastern (US)' },
                        { value: 'America/Chicago', label: 'Central (US)' },
                        { value: 'America/Denver', label: 'Mountain (US)' },
                        { value: 'America/Los_Angeles', label: 'Pacific (US)' },
                        { value: 'Europe/London', label: 'London' },
                        { value: 'Europe/Paris', label: 'Paris' },
                        { value: 'Asia/Tokyo', label: 'Tokyo' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>

                <JsonInput
                  label="Custom Metadata (JSON)"
                  placeholder='{"customField": "value"}'
                  value={metadata}
                  onChange={setMetadata}
                  validationError="Invalid JSON"
                  formatOnBlur
                  autosize
                  minRows={5}
                  maxRows={15}
                />

                <JsonInput
                  label="Custom Fields (JSON)"
                  placeholder='{"tenantSpecific": "field"}'
                  value={customFields}
                  onChange={setCustomFields}
                  validationError="Invalid JSON"
                  formatOnBlur
                  autosize
                  minRows={3}
                  maxRows={10}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb="md">
                💬 Collaboration
              </Title>
              <Stack gap="md">
                <Switch
                  label="Draft Mode"
                  description="Save as draft instead of publishing"
                  checked={draftMode}
                  onChange={(e) => setDraftMode(e.currentTarget.checked)}
                />

                <NumberInput
                  label="Feedback Score (1-5)"
                  description="User feedback rating"
                  placeholder="5"
                  value={feedbackScore}
                  onChange={(val) => setFeedbackScore(val as number)}
                  min={1}
                  max={5}
                  leftSection={<IconStar size={16} />}
                />
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Form Actions */}
      <Paper p="xl" withBorder>
        <Group justify="space-between">
          <div>
            <Text size="sm" c="dimmed" mb="xs">
              {validationErrors.filter((e) => e.severity === 'error').length === 0
                ? '✅ Ready to submit'
                : `❌ ${validationErrors.filter((e) => e.severity === 'error').length} validation error(s)`}
            </Text>
            <Group gap="xs">
              <Badge color="blue" variant="light">
                Completeness: {completenessScore}%
              </Badge>
              <Badge color="green" variant="light">
                Security: {securityScore}%
              </Badge>
              <Badge
                color={
                  criticalityLevel === 'critical'
                    ? 'red'
                    : criticalityLevel === 'immediate'
                      ? 'orange'
                      : criticalityLevel === 'high'
                        ? 'yellow'
                        : 'blue'
                }
                variant="filled"
              >
                {criticalityLevel}
              </Badge>
            </Group>
          </div>

          <Group>
            {onCancel && (
              <Button variant="light" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              loading={mutationLoading}
              disabled={validationErrors.filter((e) => e.severity === 'error').length > 0}
              leftSection={mode === 'create' ? <IconPlus size={16} /> : <IconCheck size={16} />}
              size="lg"
            >
              {mode === 'create' ? 'Create Task' : mode === 'edit' ? 'Save Changes' : 'Clone Task'}
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* MFA Modal */}
      <Modal
        opened={mfaModalOpened}
        onClose={() => setMfaModalOpened(false)}
        title="🔐 Multi-Factor Authentication"
      >
        <Stack gap="md">
          <Alert color="blue" icon={<IconShieldCheck />}>
            This task requires MFA verification
          </Alert>
          <PasswordInput
            label="MFA Code"
            placeholder="Enter 6-digit code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            maxLength={6}
          />
          <Button
            fullWidth
            onClick={() => {
              // Verify MFA and submit
              setMfaModalOpened(false);
              handleSubmit();
            }}
          >
            Verify & Submit
          </Button>
        </Stack>
      </Modal>

      {/* Save Template Modal */}
      <SaveTemplateModal
        opened={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        formState={collectCurrentState()}
        onSaved={() => setSaveTemplateOpen(false)}
      />

      {/* Template Picker Drawer */}
      <TaskTemplatePicker
        opened={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        setters={formSetters}
      />

      {/* LLM Prefill Panel */}
      <TaskPrefillPanel
        opened={prefillPanelOpen}
        onClose={() => setPrefillPanelOpen(false)}
        setters={formSetters}
      />
    </Stack>
  );
};

export default ComprehensiveTaskForm;
